import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import {
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import FormattedText from "./FormatedText";
import { getLinkPreview } from "../services/SearchApi";
import { BeachAccess } from "@mui/icons-material";
import VideoPreview from "../components/projects/VideoPreview";

const ReadMoreText = ({ text = "", maxLength = 100, useHtml = false, taggedUsers = [], chatarea }) => {
  const safeText = text || "";
  const [isExpanded, setIsExpanded] = useState(false);
  const [urlData, setUrlData] = useState(null);
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);

  // Extract URL from text, handling various formats
  const extractUrl = (text) => {
    const urlRegex =
      /((https?:\/\/)?(www\.)?[a-zA-Z0-9._-]+\.[a-zA-Z]{2,}([^\s]*)?)/g;
    const match = text.match(urlRegex);
    if (match) {
      let url = match[0];
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = `http://${url}`;
      }
      return url;
    }
    return null;
  };

  const url = extractUrl(safeText);

 useEffect(() => {
    if (!url) return;
    setIsFetchingUrl(true);
    getLinkPreview(url)
      .then((response) => {
        setUrlData(response);
        setIsFetchingUrl(false);
      })
      .catch((error) => {
        console.error("Error fetching URL metadata:", error);
        setIsFetchingUrl(false);
      });
  }, [url]);


  const toggleReadMore = () => setIsExpanded(!isExpanded);

  const getTruncatedText = (text, maxLength) => {
    if (text.length <= maxLength || isExpanded) return text;

    let trimmedText = text.substring(0, maxLength);
    let lastSpaceIndex = trimmedText.lastIndexOf(" ");

    if (lastSpaceIndex > 0) {
      trimmedText = trimmedText.substring(0, lastSpaceIndex);
    }

    return trimmedText + " ...";
  };

  const truncatedText = getTruncatedText(safeText, maxLength);

  const isPlayableVideo = (url) => {
    if (!url) return false;

    return (
      /youtube\.com|youtu\.be|vimeo\.com|soundcloud\.com|twitter\.com|x\.com|facebook\.com|instagram\.com\/(reel|p|tv)/.test(url)
    );
  };


  return (
    <div style={{ marginBottom: chatarea ? '0px' : '1.2rem' }}>
      {useHtml ? (
        <>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-line",
              overflowWrap: "break-word",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              fontSize: "0.800rem",
            }}
            dangerouslySetInnerHTML={{ __html: truncatedText }}
          />
          {safeText.length > maxLength && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ cursor: "pointer", fontSize: "0.700rem" }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleReadMore();
                }}
              >
                {isExpanded ? "Less" : "More"}
              </Typography>
            </Box>
          )}

        </>
      ) : (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-line",
            overflowWrap: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
          }}
          // onClick={(e) => {
          //   e.stopPropagation();
          //   toggleReadMore();
          // }}
        >
          <span style={{ display: "inline" }}>
            <FormattedText text={truncatedText} taggedUsers={taggedUsers} />

            {safeText.length > maxLength && (
              <Typography
                component="span"
                variant="body2"
                color="grey.600"
                sx={{ cursor: "pointer", display: "inline" }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleReadMore();
                }}
              >
                {isExpanded ? " Less" : " More"}
              </Typography>
            )}
          </span>
        </Typography>
      )}

      {isFetchingUrl ? (
        <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
          <CircularProgress size={20} sx={{ ml: 1 }} />
        </Box>
      ) : isPlayableVideo(url) ? (
        <Box sx={{ mt: 2 }} >
        <VideoPreview watchLink={url} donotshowfallback={true} />
        </Box>
      ) : urlData ? (
        <Box
          component="a"
          href={url}
          target="_blank"
          rel="noreferrer"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            p: 1,
            borderRadius: 2,
            textDecoration: "none",
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            mt: 2,
          }}
        >
          {urlData.favicon ? (
            <Box sx={{ width: 60, height: 60, overflow: "hidden" }}>
              <Box
                component="img"
                src={urlData.favicon}
                alt={urlData.title}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : (
            <BeachAccess />
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {urlData.title || "No title"}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {urlData.description || "No description"}
            </Typography>

            <Typography
              variant="caption"
              color="text.disabled"
              sx={{
                display: "block",
                mt: 0.5,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {url}
            </Typography>
          </Box>
        </Box>
      ) : null}

    </div>
  );
};

ReadMoreText.propTypes = {
  text: PropTypes.string,
  maxLength: PropTypes.number,
  useHtml: PropTypes.bool,
  taggedUsers: PropTypes.array,
};

export default ReadMoreText;